/**
 * Firebase Error Boundary
 * Catches and handles Firebase-related errors, including the "i is not a function" error
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader } from '@/components/UI/Card';
import Button from '@/components/UI/Button';
import { AlertTriangle, RefreshCw, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

export class FirebaseErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Generate a unique error ID for tracking
    const errorId = `firebase-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('Firebase Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Check for specific Firebase errors
    this.handleSpecificErrors(error);
  }

  private handleSpecificErrors(error: Error) {
    const errorMessage = error.message.toLowerCase();
    
    // Handle "i is not a function" error
    if (errorMessage.includes('i is not a function')) {
      console.error('Detected "i is not a function" error - likely Firebase minification issue');
      this.attemptFirebaseReinitialization();
    }
    
    // Handle other common Firebase errors
    if (errorMessage.includes('network error') || errorMessage.includes('failed to fetch')) {
      console.error('Network connectivity issue detected');
    }
    
    if (errorMessage.includes('auth/') || errorMessage.includes('firestore/')) {
      console.error('Firebase service error detected');
    }
  }

  private async attemptFirebaseReinitialization() {
    try {
      // Clear any cached Firebase state
      if (typeof window !== 'undefined') {
        // Clear localStorage Firebase data
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('firebase:')) {
            localStorage.removeItem(key);
          }
        });
        
        // Clear sessionStorage Firebase data
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('firebase:')) {
            sessionStorage.removeItem(key);
          }
        });
      }
      
      console.log('Cleared Firebase cache data');
    } catch (clearError) {
      console.error('Failed to clear Firebase cache:', clearError);
    }
  }

  private handleRetry = () => {
    // Clear the error state and attempt to recover
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  private handleReload = () => {
    // Force page reload as last resort
    window.location.reload();
  };

  private getErrorSuggestions(error: Error): string[] {
    const suggestions: string[] = [];
    const errorMessage = error.message.toLowerCase();
    
    if (errorMessage.includes('i is not a function')) {
      suggestions.push('This appears to be a Firebase minification issue');
      suggestions.push('Try clearing your browser cache and cookies');
      suggestions.push('Check if you have browser extensions blocking Firebase');
      suggestions.push('Verify your Firebase configuration is correct');
    }
    
    if (errorMessage.includes('network')) {
      suggestions.push('Check your internet connection');
      suggestions.push('Verify Firebase project is active');
      suggestions.push('Check if your firewall is blocking Firebase domains');
    }
    
    if (errorMessage.includes('auth/')) {
      suggestions.push('Check Firebase Authentication configuration');
      suggestions.push('Verify your API keys are correct');
      suggestions.push('Ensure Authentication is enabled in Firebase Console');
    }
    
    if (suggestions.length === 0) {
      suggestions.push('Try refreshing the page');
      suggestions.push('Clear browser cache and cookies');
      suggestions.push('Check browser console for additional errors');
    }
    
    return suggestions;
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const suggestions = this.getErrorSuggestions(this.state.error!);

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Firebase Error Detected
                  </h1>
                  <p className="text-gray-600">
                    Something went wrong with the Firebase connection
                  </p>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Error Details */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Bug className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Error Details
                    </h3>
                    <p className="text-sm text-red-700 mb-2">
                      {this.state.error?.message || 'Unknown error occurred'}
                    </p>
                    <p className="text-xs text-red-600">
                      Error ID: {this.state.errorId}
                    </p>
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Suggested Solutions
                </h3>
                <ul className="space-y-2">
                  {suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span className="text-gray-700">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={this.handleRetry}
                  className="flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Try Again</span>
                </Button>
                
                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Reload Page</span>
                </Button>
                
                <Button
                  onClick={() => window.open('/auth-debug', '_blank')}
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                >
                  <Bug className="h-4 w-4" />
                  <span>Debug Tools</span>
                </Button>
              </div>

              {/* Technical Details (Collapsible) */}
              <details className="bg-gray-50 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                  Technical Details (for developers)
                </summary>
                <div className="space-y-2 text-xs text-gray-600">
                  <div>
                    <strong>Error:</strong> {this.state.error?.name}
                  </div>
                  <div>
                    <strong>Message:</strong> {this.state.error?.message}
                  </div>
                  <div>
                    <strong>Stack:</strong>
                    <pre className="mt-1 bg-white p-2 rounded border text-xs overflow-auto">
                      {this.state.error?.stack}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 bg-white p-2 rounded border text-xs overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FirebaseErrorBoundary;
